/**
 * LEGACY — superseded by `src/features/planner/types/planner.ts` and the
 * status / scheduling schemas in `src/features/planner/schemas/` (Wave 8,
 * 2026-05-19). Scheduled for deletion in 2 sprints. DO NOT add features or
 * fixes here.
 *
 * Planner DTOs - Match backend exactly
 */

export enum PlannerActivityType {
  Job = 1,
  Training = 2,
  Meeting = 3,
  Other = 4,
}

export enum PlannerActionIntent {
  None = 0,
  Erect = 1,
  Modify = 2,
  Dismantle = 3,
  Mixed = 4,
  Inspection = 5,
  Maintenance = 6,
}

export interface ExecutionSummary {
  erectCount: number;
  modifyCount: number;
  dismantleCount: number;
  inspectionCount: number;
  maintenanceCount: number;
  generalCount?: number; // Optional for backward compatibility
}

export enum PlannerActivityStatus {
  Scheduled = 1,
  InProgress = 2,
  CompletedPendingReview = 3,
  Approved = 4,
  Rejected = 5,
  Cancelled = 6,
}

export interface PlannerCardDto {
  id: string;
  title: string;
  scheduledStart: string;
  scheduledEnd?: string; // nullable per API docs
  planningOrder?: number; // nullable per API docs
  status: number;
  actionIntent: number; // Required, PlannerActionIntent enum
  actionIntentName?: string; // Action intent name (e.g., "ERECT", "MODIFY", "DISMANTLE"), nullable
  chargeInitials?: string; // Legacy field (deprecated, use chargeType instead)
  siteTypeColorHex?: string;
  crewCount: number;
  assetCount: number;
  taskCount?: number; // Task count, nullable
  referenceId?: string; // GUID, nullable
  referenceType: number; // PlannerReferenceType enum
  supervisorUserId?: string; // GUID, nullable - Supervisor user ID
  supervisorName?: string; // Supervisor full name (FirstName + LastName), nullable
  // Site and Job related fields (from API)
  siteName?: string; // Site name, nullable
  clientName?: string; // Client name, nullable
  priorityCustomer?: boolean; // Priority customer flag from Client table
  siteTypeName?: string; // Site type name, nullable
  siteLatitude?: number; // Site latitude (from AddressPoint.Y), nullable
  siteLongitude?: number; // Site longitude (from AddressPoint.X), nullable
  siteAddress?: string; // Combined Address1 and Address2, nullable
  chargeType?: string; // 2-character charge type: "QT" (Quoted), "CU" (ChargeUp), "VQ" (VariationQuoted), nullable
  jobNumber?: string; // Job number, nullable
  activityNumber?: string; // Human-readable activity ID (e.g. ACT000124), nullable
  continuationStep?: number; // Step in continuation chain (1 = root, >1 = continuation)
  chainLength?: number; // Number of activities in continuation chain (from execution line)
  chainStartDate?: string; // ISO 8601 - Earliest activity date in chain
  chainEndDate?: string; // ISO 8601 - Latest activity date in chain
  chainActivities?: Array<{ activityNumber: string; scheduledStart: string }>; // Chain members for tooltip (optional)
  taskTypeColorHex?: string;
  notes?: string;
  rowVersion?: string; // Base64 encoded byte array, nullable
  executionSummary?: ExecutionSummary; // Optional for backward compatibility
  [key: string]: unknown;
}

// API returns array directly, not object with days property
export interface PlannerMonthDayDto {
  id: string;
  title: string;
  scheduledStart: string;
  scheduledEnd?: string;
  planningOrder?: number;
  status: number;
  siteTypeColorHex?: string;
  chargeInitials?: string;
  actionIntent: number;
  actionIntentName?: string; // Action intent name (e.g., "ERECT", "MODIFY", "DISMANTLE"), nullable
  executionSummary?: ExecutionSummary; // Optional execution summary for badges
  crewCount: number;
  activityNumber?: string; // Human-readable activity ID (e.g. ACT000124), nullable
  continuationStep?: number; // Step in continuation chain (1 = root, >1 = continuation)
  chainLength?: number; // Number of activities in continuation chain
  chainStartDate?: string; // ISO 8601 - Earliest activity date in chain
  chainEndDate?: string; // ISO 8601 - Latest activity date in chain
  [key: string]: unknown; // Allow additional properties
}

// Month summary returns array directly
export type PlannerMonthDto = PlannerMonthDayDto[];

export interface PlannerCrewItemDto {
  userId: string;
  name: string;
  role?: string;
  avatarUrl?: string;
  [key: string]: unknown;
}

export interface PlannerCrewLoadDto {
  date: string;
  available: PlannerCrewItemDto[];
  assigned?: PlannerCrewItemDto[];
  [key: string]: unknown;
}

export interface PlannerAssetItemDto {
  assetId: string;
  name: string;
  icon?: string;
  assetClass?: number; // 1=Vehicle, 2=Tool, 3=Equipment
  assetImage?: string; // Blob path/URL for asset image
  [key: string]: unknown;
}

export interface PlannerAssetLoadDto {
  date: string;
  available: PlannerAssetItemDto[];
  assigned?: PlannerAssetItemDto[];
  [key: string]: unknown;
}

export interface PlannerOrderUpdateDto {
  activityId: string;
  planningOrder: number;
}

export interface PlannerReorderPayload {
  updates: PlannerOrderUpdateDto[];
}

export interface PlannerActivityTimePayload {
  newStart: string; // Changed from scheduledStart per API docs
  newEnd?: string; // Changed from scheduledEnd per API docs
  rowVersion?: string; // Optional for concurrency control
}

export interface PlannerCrewAssignmentsPayload {
  crewIds: string[];
}

export interface PlannerAssetAssignmentsPayload {
  assetIds: string[];
}

export interface PlannerSingleCrewAssignmentPayload {
  crewMemberId: string;
  isSupervisor?: boolean;
  rowVersion?: string;
}

export interface PlannerSingleAssetAssignmentPayload {
  assetId: string;
  rowVersion?: string;
}

export interface PlannerActivityCreatePayload {
  activityType: number; // PlannerActivityType enum (1=Job, 2=Training, 3=Meeting, 4=Other)
  actionIntent: number; // PlannerActionIntent enum (required)
  clientId?: string; // Required if activityType = Job
  siteId?: string; // Required if activityType = Job
  jobId?: string; // Required if activityType = Job
  supervisorUserId: string; // GUID, Required - Supervisor user ID responsible for overseeing activity execution
  scheduledStart: string; // ISO 8601 DateTime, Required
  scheduledEnd?: string; // ISO 8601 DateTime, nullable
  notes?: string; // max 2000 chars, nullable
  crewUserIds?: string[]; // GUID array, nullable (Both POST and PUT use crewUserIds)
  assetIds?: string[]; // GUID array, nullable
}

export interface PlannerActivityUpdatePayload {
  rowVersion?: string; // Base64 encoded byte array, nullable (optional but recommended for concurrency control)
  supervisorUserId: string; // GUID, Required - Supervisor user ID responsible for overseeing activity execution
  actionIntent?: number; // PlannerActionIntent enum, nullable
  scheduledStart?: string; // ISO 8601 DateTime, nullable
  scheduledEnd?: string; // ISO 8601 DateTime, nullable
  notes?: string; // max 2000 chars, nullable
  crewUserIds?: string[]; // GUID array, nullable (Both POST and PUT use crewUserIds - replaces existing crew)
  assetIds?: string[]; // GUID array, nullable (replaces existing assets)
}

export interface ChangeActivityStatusPayload {
  newStatus: PlannerActivityStatus; // Required
  rowVersion?: string; // Base64 encoded byte array, nullable
}

export type PlannerViewMode = 'day' | 'week' | 'month';

export interface PlannerUnplannedWorkDto {
  workOrderTaskId: string; // GUID, Required
  jobId: string; // GUID, Required
  jobNumber: string; // Required
  jobName: string; // Required (this is the task/job name)
  workOrderId: string; // GUID, Required
  workOrderTaskDescription?: string; // Task description from WorkOrderTask, nullable
  clientName: string; // Required
  siteName: string; // Required
  scaffoldId?: string; // GUID, nullable
  scaffoldCode?: string; // nullable
  taskType?: number; // ScaffoldTaskType, nullable
  actionIntent: number; // PlannerActionIntent enum, Required
  actionIntentName: string; // String name of ActionIntent, Required
  requestedStart?: string; // ISO 8601 DateTime, nullable
  requestedEnd?: string; // ISO 8601 DateTime, nullable
}

export enum PlannerExecutionStatus {
  Planned = 0,
  Completed = 1,
  Partial = 2,
  NotDone = 3,
}

export enum ScaffoldTaskType {
  Erect = 1,
  Modify = 2,
  Dismantle = 3,
  Inspection = 4,
  Maintenance = 5,
  General = 6,
}

export interface PlannerExecutionLineDto {
  id: string; // GUID
  plannerActivityId: string; // GUID
  workOrderTaskId: string; // GUID
  workOrderTaskNumber?: string; // nullable, excluded from JSON when null
  workOrderTaskDescription?: string; // nullable (maps to WorkOrderTask.TaskDescription), excluded from JSON when null
  scaffoldId?: string; // GUID, nullable
  scaffoldNumber?: string; // nullable, excluded from JSON when null
  scaffoldTaskType: ScaffoldTaskType; // number: 1-6 (Erect=1, Modify=2, Dismantle=3, Inspection=4, Maintenance=5, General=6)
  scaffoldTaskTypeName: string; // Human-readable: "Erect", "Modify", "Dismantle", "Inspection", "Maintenance", "General"
  executionStatus: PlannerExecutionStatus; // number: 0=Planned, 1=Partial, 2=Completed
  executionStatusName: string; // Human-readable: "Planned", "Partial", "Completed"
  notes?: string; // max 2000 chars, nullable
  createdAt: string; // ISO 8601 DateTime
  executionOrder?: number; // optional, reserved for future use
  
  // Continuation chain metadata (computed at read time)
  isContinuation?: boolean; // True when continuationStep > 1
  continuationStep?: number; // Step number in continuation chain (1 for root)
  chainStartDate?: string; // ISO 8601 DateTime - Earliest activity date in chain
  chainEndDate?: string; // ISO 8601 DateTime - Latest activity date in chain
  chainLength?: number; // Number of activities in continuation chain
  chainActivities?: Array<{ activityNumber: string; scheduledStart: string }>; // Chain members for tooltip (optional)
  continuesForward?: boolean; // True when task continues in later activity
  continuedFromPrevious?: boolean; // True when task started in earlier activity
}

/**
 * Available WorkOrderTask for planner activity (from GET /planner/{activityId}/available-work-order-tasks)
 */
export interface PlannerAvailableWorkOrderTaskDto {
  id: string;
  taskNumber?: string;
  taskDescription?: string;
  workOrderId?: string;
  workOrderNumber?: string;
  workItemType?: number;
  scaffoldTaskType?: number;
  scaffoldId?: string;
  scaffoldNumber?: string;
  scaffoldName?: string;
}

export interface CreatePlannerExecutionLineRequest {
  workOrderTaskId: string; // GUID, Required
  scaffoldId?: string; // GUID, nullable
  scaffoldTaskType: ScaffoldTaskType; // Required
  executionStatus?: PlannerExecutionStatus; // Optional, defaults to Planned (0) if not provided
  notes?: string; // max 2000 chars, nullable
}

export interface AddExecutionLinesPayload {
  lines: CreatePlannerExecutionLineRequest[]; // Required, min length 1
}

export interface ExecutionCaptureResult {
  executionLineId: string; // GUID, Required
  scaffoldId?: string; // GUID, nullable
  result: "Completed" | "Partial" | "NotDone"; // Required
  continueOn?: string; // ISO 8601 DateTime, Required if result = "Partial" or "NotDone"
}

export interface ExecutionCapturePayload {
  activityId: string; // GUID, Required - Must match route parameter
  results: ExecutionCaptureResult[]; // Required, min length 1
}

export interface PlannerSuggestionDto {
  jobId: string; // GUID
  jobNumber: string; // Job number/code
  siteId: string; // GUID
  siteName: string; // Site name
  actionIntent: PlannerActionIntent; // Action intent enum value
  actionIntentName: string; // Human-readable string name: "Erect", "Modify", "Dismantle", etc.
  taskCount: number; // Number of unplanned tasks in this group (always >= 2)
}

export interface CreateActivityFromUnplannedTaskGroupRequest {
  jobId: string; // GUID, Required
  siteId: string; // GUID, Required
  actionIntent: PlannerActionIntent; // Required
  supervisorUserId: string; // GUID, Required - Supervisor user ID responsible for overseeing activity execution
  startDateTime: string; // ISO 8601 DateTime, Required
  endDateTime: string; // ISO 8601 DateTime, Required
  crewIds?: string[]; // GUID array, optional
  assetIds?: string[]; // GUID array, optional
}

export interface CloneActivityRequest {
  actionIntent: PlannerActionIntent; // Required
  supervisorUserId: string; // GUID, Required - Supervisor user ID responsible for overseeing activity execution
  startDateTime?: string; // ISO 8601 DateTime, optional - Overrides original ScheduledStart
  endDateTime?: string; // ISO 8601 DateTime, optional - Overrides original ScheduledEnd
  workOrderTaskId?: string; // GUID, optional - Creates an execution line for this task
}

export interface SiteWorkloadSummaryDto {
  clientName: string; // Client name
  siteName: string; // Site name
  activityCount: number; // Number of activities scheduled for this site on the date
  totalCrewAssigned: number; // Total number of crew members assigned across all activities
  totalAssetsAssigned: number; // Total number of assets assigned across all activities
}
